import { onScopeDispose, readonly, ref } from 'vue'

/**
 * Tracks the height (in px) of the **visual viewport** — the on-screen area not
 * covered by the mobile keyboard.
 *
 * Our meta viewport uses the browser default (`interactive-widget=resizes-visual`),
 * so when the keyboard opens the *layout* viewport keeps its full height: `100dvh`
 * elements stay full-size and their lower content (footers, list rows) hide behind
 * the keyboard. Binding a full-screen container's height to this value instead
 * keeps it sized to what the user can actually see, so the content compresses into
 * the space above the keyboard rather than sliding under it.
 *
 * Uses the VisualViewport API (supported on iOS Safari and Android Chrome) and
 * falls back to `window.innerHeight` where it's unavailable.
 */
export function useVisualViewportHeight() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  const height = ref(vv?.height ?? window.innerHeight)

  function update() {
    height.value = vv?.height ?? window.innerHeight
  }

  if (vv) {
    // `resize` covers the keyboard opening/closing; `scroll` keeps the value in
    // sync when the browser shifts the viewport to reveal a focused field.
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    onScopeDispose(() => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    })
  }

  return readonly(height)
}
